import React, { FC, useRef } from 'react';

import { AppLayoutBreakpoint, useContainerWidth } from '../../../../util/responsiveness';
import { getGutterValueInPixels } from '../../../../util/styles/gutters';
import Flex, { FlexItem } from '../../../Flex';
import Scrollable from '../../../Scrollable';
import AppLayoutRow from '../../AppLayoutRow';
import ContentContainer from '../../ContentContainer';
import {
  useAppLayoutGutterSize,
  useContentContainerContext,
  useContentMaxWidth,
  useElementHeight,
  useIsContentSidebarCurrentlyHiddenByNarrowWidth,
} from '../../hooks';

import MainAreaHeroBackground from './MainAreaHeroBackground';
import { getClassNames } from './MainAreaRow.styles';
import { MainAreaRowProps } from './MainAreaRow.types';
import MainAreaRowContent from './MainAreaRowContent';
import SidebarPanelButtonStickyColumn from './SidebarPanelButtonStickyColumn';
import { showSubHeaderBreakpoint } from './SubHeaderRow.types';

export const contentSidebarCollapseBreakpoint = AppLayoutBreakpoint.Width812;
const leftNavColumnMinWidth = 260;
const leftNavColumnMaxWidth = 360;

const getMaxWidth = (
  shouldFillAvailableSpace: boolean,
  hasLeftNav: boolean,
  gutterSize: number,
  contentMaxWidth: number | undefined,
  navColumnMaxWidth: number
) => {
  if (shouldFillAvailableSpace || !contentMaxWidth) {
    return '100%';
  }

  const navColumnWidth = hasLeftNav ? navColumnMaxWidth : 0;
  const navColumnGutter = hasLeftNav ? getGutterValueInPixels(gutterSize) : 0;
  const navColumnTotalWidth = navColumnWidth + navColumnGutter;

  return navColumnTotalWidth + contentMaxWidth;
};

const MainAreaRow: FC<MainAreaRowProps> = ({ children, leftNav, mainAreaBackgroundSrc }) => {
  const { shouldFillAvailableSpace, shouldDisableScroll } = useContentContainerContext();

  const containerWidth = useContainerWidth();
  const contentMaxWidth = useContentMaxWidth();
  const appLayoutGutterSize = useAppLayoutGutterSize();
  const gutterSize = shouldFillAvailableSpace ? 0 : appLayoutGutterSize;

  const isSidebarSuppressed = useIsContentSidebarCurrentlyHiddenByNarrowWidth();
  const shouldSidebarPanelButtonBeDisplayedAtBreakpoint = containerWidth >= showSubHeaderBreakpoint;
  const sidebarPanelButton =
    isSidebarSuppressed && shouldSidebarPanelButtonBeDisplayedAtBreakpoint ? <SidebarPanelButtonStickyColumn /> : null;

  const isLeftNavSuppressed = containerWidth < showSubHeaderBreakpoint;
  const hasLeftNav = !!leftNav && !isLeftNavSuppressed && !shouldFillAvailableSpace;
  const hasSidebarPanelButton = !!sidebarPanelButton;
  const navColumnWidth =
    containerWidth >= AppLayoutBreakpoint.Width1416 ? leftNavColumnMaxWidth : leftNavColumnMinWidth;

  const maxWidth = getMaxWidth(shouldFillAvailableSpace, hasLeftNav, gutterSize, contentMaxWidth, navColumnWidth);

  const mainAreaRef = useRef(null);
  const mainAreaHeight = useElementHeight(mainAreaRef.current);

  const classNames = getClassNames(mainAreaHeight, navColumnWidth, gutterSize);
  const scrollbarOffset = getGutterValueInPixels(3);

  const leftNavigation = hasLeftNav ? (
    <FlexItem width={navColumnWidth} shrink={0}>
      <div className={classNames.leftNavPositionWrapper}>
        <Scrollable scrollbarOffset={scrollbarOffset}>
          <div className={classNames.scrollablePaddingWrapper}>{leftNav}</div>
        </Scrollable>
      </div>
    </FlexItem>
  ) : null;

  const paddingLeft = hasLeftNav ? gutterSize : undefined;
  const outerRowJustify = hasSidebarPanelButton ? 'space-between' : 'center';

  const mainAreaContent = (
    <Flex justifyContent={outerRowJustify} grow={1} height="100%" ref={mainAreaRef}>
      <Flex minWidth="auto" grow={1} width="100%" maxWidth={maxWidth} paddingLeft={paddingLeft}>
        {leftNavigation}
        <ContentContainer disableScrolling={true} paddingTop={gutterSize}>
          <MainAreaRowContent>{children}</MainAreaRowContent>
        </ContentContainer>
      </Flex>
      {sidebarPanelButton}
    </Flex>
  );

  return (
    <AppLayoutRow grow={1} enableScrolling={!shouldDisableScroll} forceScrollable={shouldFillAvailableSpace}>
      <MainAreaHeroBackground imgSrc={mainAreaBackgroundSrc}>{mainAreaContent}</MainAreaHeroBackground>
    </AppLayoutRow>
  );
};
export default MainAreaRow;
